<template>
  <v-container fluid class="report-purchase-wrapper">
    <div class="report-purchase-container">
      <KpiCards class="mb-3" :items="kpiItems" :loading="!summaries.length" :skeleton="5" />

      <!-- MAIN REPORT CARD (POSSMART V2 AP-CARD STYLE) -->
      <v-card class="ap-card report-main-card" outlined elevation="0">
        <!-- Card Header with Accent and Actions -->
        <v-card-title
          class="py-3 px-4 border-bottom d-flex align-center justify-space-between flex-wrap ap-card-title"
        >
          <div class="d-flex align-center flex-wrap" style="gap: 10px;">
            <span class="ap-card-accent"></span>
            <span class="font-weight-bold text-subtitle-2">{{
              $t("report_purchase") || "ລາຍງານການສັ່ງຊື້"
            }}</span>
            <span class="ap-card-subtitle d-none d-sm-inline">
              ({{
                $t("check_and_track_purchases") ||
                "ກວດສອບ ແລະ ຕິດຕາມລາຍການສັ່ງຊື້ສິນຄ້າເຂົ້າສະຕັອກ"
              }})
            </span>
            <v-chip
              x-small
              color="primary"
              outlined
              class="font-weight-bold ml-1"
            >
              {{ pagination.total }} {{ $t("items") }}
            </v-chip>
          </div>

          <!-- Top Action: Export to Excel -->
          <div class="d-flex align-center" style="gap: 8px;">
            <v-btn
              depressed
              outlined
              color="success"
              class="font-weight-bold text-caption rounded-lg px-3"
              height="34"
              :loading="exporting"
              @click="exportToExcel()"
            >
              <v-icon left small>fal fa-file-excel</v-icon>
              {{ $t("export_excel") }}
            </v-btn>
          </div>
        </v-card-title>

        <!-- Filter Controls Area -->
        <div class="filter-controls-area px-4 py-3 border-bottom">
          <!-- Status Segmented Tabs (Matches /manage-waste .br-tabs style) -->
          <div class="br-tabs mb-3">
            <div
              v-for="st in statusList"
              :key="st.value"
              class="br-tab"
              :class="{ 'br-tab--active': status.value === st.value }"
              @click="selectStatus(st)"
            >
              {{ st.text }}
              <span
                v-if="getStatusCount(st.value) !== null"
                class="br-tab-count"
              >
                {{ getStatusCount(st.value) }}
              </span>
            </div>
          </div>

          <!-- Main Filters Row: Search + Date Range -->
          <v-row dense align="center">
            <!-- Search Input -->
            <v-col cols="12" md="4" lg="4">
              <TextSearch
                v-model="search"
                @search="fetchItem(1)"
                @onClear="
                  search = '';
                  fetchItem(1);
                "
              />
            </v-col>

            <!-- Start Date -->
            <v-col cols="12" sm="6" md="3" lg="3">
              <DatePicker
                :dense="true"
                :placeholder="false"
                label="start_date"
                v-model="start_date"
                hide-details
              />
            </v-col>

            <!-- End Date -->
            <v-col cols="12" sm="6" md="3" lg="3">
              <DatePicker
                :dense="true"
                :placeholder="false"
                label="end_date"
                v-model="end_date"
                hide-details
              />
            </v-col>

            <!-- Reset Filter Link / Spacer -->
            <v-col
              cols="12"
              md="2"
              lg="2"
              class="d-flex justify-end align-center"
            >
              <v-btn
                text
                small
                color="primary"
                class="text-caption font-weight-medium"
                @click="resetFilters"
              >
                <v-icon left x-small>fal fa-redo</v-icon>
                {{ $t("reset") || "ຣີເຊັດ" }}
              </v-btn>
            </v-col>
          </v-row>
        </div>

        <!-- Data Table Container -->
        <div class="table-content-area flex-grow-1 overflow-x-auto">
          <v-data-table
            :headers="headers"
            :items="stockList"
            item-key="id"
            hide-default-footer
            class="purchase-table elevation-0"
            :loading="overlay && !exporting"
            :loading-text="$t('loading')"
            fixed-header
            :items-per-page="per_page"
          >
            <!-- Row Index -->
            <template slot="item.index" slot-scope="{ index }">
              <span class="ps-row-index">
                {{
                  (pagination.current_page - 1) * pagination.per_page +
                  index +
                  1
                }}
              </span>
            </template>

            <!-- Purchase Order Code with Copy Button -->
            <template slot="item.code" slot-scope="{ item }">
              <div class="d-flex align-center">
                <span class="ps-product-code font-weight-bold">{{
                  item.code
                }}</span>
                <button
                  type="button"
                  class="ps-copy-code ml-1"
                  :aria-label="$t('copy')"
                  :title="$t('copy')"
                  @click.stop="copyCode(item.code)"
                >
                  <v-icon x-small>fal fa-copy</v-icon>
                </button>
              </div>
            </template>

            <!-- Status Badge (Matching PosSmart v2 stock-pill Style) -->
            <template slot="item.status" slot-scope="{ item }">
              <span class="stock-pill" :class="statusPillClass(item.status)">
                {{ statusLabel(item.status) }}
              </span>
            </template>

            <!-- Maker (User Avatar + Name) -->
            <template slot="item.user.first_name" slot-scope="{ item }">
              <div class="d-flex align-center" style="gap: 6px;">
                <v-avatar
                  size="24"
                  color="primary lighten-5"
                  class="ps-user-avatar"
                >
                  <span
                    class="primary--text font-weight-bold"
                    style="font-size: 11px;"
                  >
                    {{
                      item.user && item.user.first_name
                        ? item.user.first_name.charAt(0).toUpperCase()
                        : "U"
                    }}
                  </span>
                </v-avatar>
                <span class="text-caption font-weight-medium text-truncate">
                  {{ item.user ? item.user.first_name : "—" }}
                </span>
              </div>
            </template>

            <!-- Branch -->
            <template slot="item.branch.name" slot-scope="{ item }">
              <span class="text-caption grey--text text--darken-2">
                {{ item.branch ? item.branch.name : "—" }}
              </span>
            </template>

            <!-- Item Count -->
            <template slot="item.stock_history_count" slot-scope="{ item }">
              <v-chip
                x-small
                outlined
                color="grey darken-1"
                class="font-weight-medium"
              >
                {{ item.stock_history_count || 0 }} {{ $t("items") }}
              </v-chip>
            </template>

            <!-- Sum Cost (Price Buy) -->
            <template slot="item.sum_price_buy" slot-scope="{ item }">
              <span
                class="ps-total-value ps-total-value--danger text-right d-block font-numeric"
              >
                {{ item.sum_price_buy | NumberFormat }} ₭
              </span>
            </template>

            <!-- Sum Projected Sales (Price Sale) -->
            <template slot="item.sum_price_sale" slot-scope="{ item }">
              <span class="ps-price-value text-right d-block font-numeric">
                {{ item.sum_price_sale | NumberFormat }} ₭
              </span>
            </template>

            <!-- Date -->
            <template slot="item.date" slot-scope="{ item }">
              <span
                class="text-caption text-right d-block text-no-wrap grey--text text--darken-1 font-numeric"
              >
                {{ item.date | created_at }}
              </span>
            </template>

            <!-- Actions (Matches PosSmart v2 .ps-act Button) -->
            <template slot="item.actions" slot-scope="{ item }">
              <div class="ps-row-actions justify-end">
                <button
                  type="button"
                  class="ps-act"
                  :title="$t('view')"
                  @click="viewItem(item)"
                >
                  <v-icon x-small>fal fa-eye</v-icon>
                </button>
              </div>
            </template>

            <!-- Empty State -->
            <template slot="no-data">
              <div class="ps-empty text-center">
                <v-icon size="48" class="ps-empty-icon mb-2"
                  >fal fa-clipboard-list</v-icon
                >
                <div class="ps-empty-text font-weight-bold">
                  {{ $t("no_data") }}
                </div>
                <div class="ps-empty-subtext mt-1 text-caption">
                  {{
                    $t("no_purchase_records_found") ||
                    "ບໍ່ພົບຂໍ້ມູນການສັ່ງຊື້ສິນຄ້າຕາມເງື່ອນໄຂທີ່ເລືອກ"
                  }}
                </div>
              </div>
            </template>
          </v-data-table>
        </div>

        <!-- Table Pagination Footer -->
        <div
          class="table-pagination-footer d-flex align-center justify-space-between flex-wrap px-4 py-2 border-top"
        >
          <div
            class="d-flex align-center text-caption grey--text text--darken-1"
          >
            <span>{{ $t("rows_per_page") }}:</span>
            <v-select
              v-model="per_page"
              :items="rowsPerPageItems"
              dense
              outlined
              hide-details
              class="rows-select ml-2 mr-3"
              style="max-width: 80px;"
              @change="onPerPageChange"
            />
            <span v-if="pagination.total">
              {{ (pagination.current_page - 1) * pagination.per_page + 1 }}-{{
                Math.min(
                  pagination.current_page * pagination.per_page,
                  pagination.total
                )
              }}
              {{ $t("of") || "ຈາກ" }} {{ pagination.total }}
            </span>
          </div>

          <div class="d-flex align-center" style="gap: 6px;">
            <v-btn
              icon
              small
              outlined
              class="rounded-lg pagination-btn"
              :disabled="pagination.current_page <= 1"
              @click="goToPage(-1)"
            >
              <v-icon small>fal fa-chevron-left</v-icon>
            </v-btn>
            <span class="text-caption font-weight-bold px-2">
              {{ pagination.current_page }} / {{ pagination.last_page || 1 }}
            </span>
            <v-btn
              icon
              small
              outlined
              class="rounded-lg pagination-btn"
              :disabled="pagination.current_page >= pagination.last_page"
              @click="goToPage(1)"
            >
              <v-icon small>fal fa-chevron-right</v-icon>
            </v-btn>
          </div>
        </div>
      </v-card>

      <!-- View Item Dialog -->
      <app-custom-dialog
        width="1000px"
        v-model="dialog"
        :footer="false"
        title="description"
      >
        <div slot="activator">
          <AppDetail :item="form" @viewItem="productDetail" />
          <template v-if="form.status === 'pending'">
            <v-divider class="my-4" />
            <div class="d-flex align-center justify-end" style="gap: 12px;">
              <v-btn
                color="error"
                depressed
                outlined
                class="font-weight-bold rounded-lg px-4"
                @click="purchaseAction(form.id, 'rejected')"
              >
                <v-icon left small>fal fa-times</v-icon>
                {{ $t("cancel") }}
              </v-btn>
              <v-btn
                depressed
                color="primary"
                class="font-weight-bold rounded-lg px-5"
                @click="purchaseAction(form.id, 'approved')"
              >
                <v-icon left small>fal fa-check</v-icon>
                {{ $t("approved") }}
              </v-btn>
            </div>
          </template>
        </div>
      </app-custom-dialog>

      <!-- Product Detail Dialog -->
      <app-custom-dialog
        v-model="dialogDetail"
        :footer="false"
        title="description"
      >
        <div slot="activator">
          <AppProductDetail :item="product_detail" />
        </div>
      </app-custom-dialog>

      <!-- Loading Overlay -->
      <app-loading :overlay="overlay && !exporting" />
    </div>
  </v-container>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import { historyStock } from "@/utils/head";
import { sizeValidate } from "@/validations/shop";
import { myMixin } from "@/mixin/main";
import AppDetail from "@/components/products/stockHistory.vue";
import DatePicker from "@/components/global/DatePicker";
import TextSearch from "@/components/global/TextSearch";
import { success, confirm, error, toast } from "@/controllers/alert";
import { purchaseStatus } from "@/utils/mock";
import AppProductDetail from "@/components/products/details";
import Help from "@/utils/helper";

// Get today date (YYYY-MM-DD)
const today = new Date(Date.now() - new Date().getTimezoneOffset() * 60000)
  .toISOString()
  .substr(0, 10);

// Get tomorrow date (YYYY-MM-DD)
const tomorrow = new Date(
  Date.now() - new Date().getTimezoneOffset() * 60000 + 86400000
)
  .toISOString()
  .substr(0, 10);

export default {
  mixins: [sizeValidate, myMixin],

  data: () => ({
    dialog: false,
    dialogDetail: false,
    overlay: false,
    exporting: false,
    search: "",
    product_detail: {},
    status: {
      text: "ທັງໝົດ",
      value: "all",
    },
    form: {
      unit: "piece",
    },
    start_date: today,
    end_date: tomorrow,
    per_page: 25,
  }),

  created() {
    this.fetchItem(1);
  },

  watch: {
    status() {
      this.fetchItem(1);
    },
    start_date(newVal, oldVal) {
      if (oldVal !== undefined && newVal !== oldVal) {
        this.handleDateChange();
      }
    },
    end_date(newVal, oldVal) {
      if (oldVal !== undefined && newVal !== oldVal) {
        this.handleDateChange();
      }
    },
  },

  components: {
    AppDetail,
    DatePicker,
    TextSearch,
    AppProductDetail,
  },

  computed: {
    kpiItems() {
      return this.summaries.map((item) => ({
        key: item.title,
        title: item.title,
        icon: item.icon,
        value: this.formatSummaryValue(item),
        note: item.description ? this.$t(item.description) : "",
      }));
    },
    ...mapGetters({
      stocks: "index/stocks",
      rowsPerPageItems: "index/rowsPerPageItems",
    }),

    headers() {
      return historyStock(this);
    },

    statusList() {
      return purchaseStatus(this);
    },

    stockList() {
      if (!this.stocks) return [];
      if (Array.isArray(this.stocks)) return this.stocks;
      if (Array.isArray(this.stocks.data)) return this.stocks.data;
      return [];
    },

    pagination() {
      if (!this.stocks || Array.isArray(this.stocks)) {
        return {
          total: this.stockList.length,
          current_page: 1,
          per_page: this.per_page,
          last_page: 1,
          from: 1,
          to: this.stockList.length,
        };
      }
      return {
        total:
          this.stocks.total != null ? this.stocks.total : this.stockList.length,
        current_page: this.stocks.current_page || 1,
        per_page: this.stocks.per_page || this.per_page,
        last_page: this.stocks.last_page || 1,
        from: this.stocks.from || 1,
        to: this.stocks.to || this.stockList.length,
      };
    },

    kpiSummary() {
      const list = this.stockList;
      let totalOrders = this.pagination.total || list.length;
      let totalItems = 0;
      let totalPriceBuy = 0;
      let totalPriceSale = 0;
      let pendingCount = 0;
      let approvedCount = 0;
      let rejectedCount = 0;

      list.forEach((item) => {
        totalItems += Number(item.stock_history_count || 0);
        totalPriceBuy += Number(item.sum_price_buy || 0);
        totalPriceSale += Number(item.sum_price_sale || 0);
        const st = String(item.status || "").toLowerCase();
        if (st === "pending") pendingCount++;
        else if (st === "approved") approvedCount++;
        else if (st === "rejected" || st === "cancel") rejectedCount++;
      });

      const profit = totalPriceSale - totalPriceBuy;
      const profitMargin =
        totalPriceBuy > 0 ? ((profit / totalPriceBuy) * 100).toFixed(1) : 0;

      return {
        totalOrders,
        totalItems,
        totalPriceBuy,
        totalPriceSale,
        profit,
        profitMargin,
        pendingCount,
        approvedCount,
        rejectedCount,
      };
    },

    summaries() {
      return [
        {
          icon: "fal fa-boxes",
          title: "orders",
          total: this.kpiSummary.totalOrders,
          suffix: "",
          description: `${this.kpiSummary.totalItems} ${
            this.$t("units") || "ລາຍການ"
          }`,
        },
        {
          icon: "fal fa-clock",
          title: "pending",
          total: this.kpiSummary.pendingCount,
          suffix: "",
          description: this.$t("waiting_approval") || "ລໍຖ້າອະນຸມັດ",
        },
        {
          icon: "fal fa-arrow-down",
          title: "price_buy",
          total: this.kpiSummary.totalPriceBuy,
          suffix: " ₭",
          description: "",
        },
        {
          icon: "fal fa-arrow-up",
          title: "price_sale",
          total: this.kpiSummary.totalPriceSale,
          suffix: " ₭",
          description: "",
        },
        {
          icon: "fal fa-chart-line",
          title: "profit",
          total: this.kpiSummary.profit,
          suffix: " ₭",
          description: `${this.kpiSummary.profitMargin >= 0 ? "+" : ""}${
            this.kpiSummary.profitMargin
          }% margin`,
        },
      ];
    },
  },

  methods: {
    ...mapActions({
      fetch: "index/fetch",
    }),

    formatSummaryValue(item) {
      if (item.value !== undefined) return item.value;
      const num = this.$options.filters.NumberFormat(item.total);
      return item.suffix ? `${num}${item.suffix}` : num;
    },

    async fetchItem(page = 1, perPage = this.per_page) {
      this.overlay = true;
      try {
        const body = {
          that: this,
          url: `stock?status=${this.status.value}&start_date=${this.start_date}&end_date=${this.end_date}&q=${this.search}&page=${page}&per_page=${perPage}`,
          commit: "SET_STOCKS",
        };
        await this.fetch(body);
      } catch (err) {
        console.error("fetchItem error", err);
      } finally {
        this.overlay = false;
      }
    },

    selectStatus(st) {
      if (this.status.value === st.value) return;
      this.status = st;
    },

    getStatusCount(val) {
      if (val === "all") return this.pagination.total || this.stockList.length;
      if (val === "pending") return this.kpiSummary.pendingCount;
      if (val === "approved") return this.kpiSummary.approvedCount;
      if (val === "rejected") return this.kpiSummary.rejectedCount;
      return null;
    },

    resetFilters() {
      this.search = "";
      this.status = { text: "ທັງໝົດ", value: "all" };
      this.start_date = today;
      this.end_date = tomorrow;
      this.fetchItem(1);
    },

    statusPillClass(status) {
      if (!status) return "";
      const s = String(status).toLowerCase();
      if (s === "approved" || s === "completed" || s === "success") {
        return "stock-pill-normal";
      }
      if (s === "pending" || s === "waiting") {
        return "stock-pill-low";
      }
      if (s === "rejected" || s === "cancel" || s === "declined") {
        return "stock-pill-out";
      }
      return "stock-pill-normal";
    },

    statusLabel(status) {
      if (!status) return "—";
      return this.$t(status) || status;
    },

    copyCode(code) {
      if (!code) return;
      if (navigator.clipboard?.writeText) {
        navigator.clipboard.writeText(String(code));
      }
      toast(this, "copy_success");
    },

    onPerPageChange(val) {
      this.per_page = Number(val);
      const next = { page: 1, itemsPerPage: this.per_page };
      const prev = {
        page: this.pagination.current_page,
        itemsPerPage: this.per_page,
      };
      if (this.stocks && this.stocks.first_page_url) {
        const url = Help.paginate(this.stocks, next, prev);
        this.fetch({ url, commit: "SET_STOCKS", that: this });
      } else {
        this.fetchItem(1, this.per_page);
      }
    },

    goToPage(delta) {
      const current = this.pagination.current_page;
      const last = this.pagination.last_page;
      const nextPage = current + delta;
      if (nextPage < 1 || nextPage > last) return;
      const next = { page: nextPage, itemsPerPage: this.per_page };
      const prev = { page: current, itemsPerPage: this.per_page };
      if (this.stocks && this.stocks.first_page_url) {
        const url = Help.paginate(this.stocks, next, prev);
        this.fetch({ url, commit: "SET_STOCKS", that: this });
      } else {
        this.fetchItem(nextPage, this.per_page);
      }
    },

    async viewItem({ id }) {
      try {
        this.overlay = true;
        const { data } = await this.$http.get(`stock/${id}`);
        this.form = data.data;
        this.dialog = true;
      } catch (err) {
        console.error("viewItem error", err);
      } finally {
        this.overlay = false;
      }
    },

    async handleDateChange() {
      try {
        await Help.getDifferentMonth(this, this.start_date, this.end_date);
        this.overlay = true;
        setTimeout(() => {
          this.fetchItem(1);
          this.overlay = false;
        }, 600);
      } catch (err) {
        console.log("error", err.message);
        error(this, err.message);
        this.overlay = false;
      }
    },

    async exportToExcel() {
      try {
        await Help.getDifferentMonth(this, this.start_date, this.end_date);
        this.exporting = true;
        const url = `stock_remain_period_export?start_date=${this.start_date}&end_date=${this.end_date}`;
        const response = await this.$http.get(url, {
          responseType: "blob",
        });

        const blob = response?.data;
        if (!blob) {
          error(this, "excel_export_failed");
          return;
        }

        const blobUrl = window.URL.createObjectURL(new Blob([blob]));
        const link = document.createElement("a");
        const fileName = `stock_remain_period_${this.start_date}_to_${this.end_date}.xlsx`;

        link.href = blobUrl;
        link.setAttribute("download", fileName);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        window.URL.revokeObjectURL(blobUrl);
        toast(this, "excel_export_success");
      } catch (err) {
        console.error("Error exporting Excel:", err);
        const msg =
          err?.response?.data?.message || err?.message || "excel_export_failed";
        error(this, typeof msg === "string" ? msg : "excel_export_failed");
      } finally {
        this.exporting = false;
      }
    },

    async purchaseAction(id, status) {
      const msg =
        status === "approved" ? "is_approved_purchase" : "is_cancel_purchase";
      const { isConfirmed } = await confirm({
        props: this,
        text: msg,
      });
      if (isConfirmed) {
        const form = {
          id: id,
          status: status,
        };
        const { data } = await this.$http.post(`stock_update`, form);
        if (data.status) {
          this.dialog = false;
          await Help.clearForm(this.form, this);
          await this.fetchItem(this.pagination.current_page);
          await success(this, data.message);
        }
      }
    },

    async productDetail({ product }) {
      try {
        const { data } = await this.$http.get(`products/${product.id}`);
        if (data.status) {
          this.product_detail = data.data;
          this.dialogDetail = !this.dialogDetail;
        }
      } catch (err) {
        console.error(err);
      }
    },
  },
};
</script>

<style scoped>
.report-purchase-wrapper {
  min-height: calc(100vh - 65px);
  display: flex;
  flex-direction: column;
}

.report-purchase-container {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

/* ===================================================
   KPI Summary Grid & Cards (Matches /report-bill Page Card Summary)
   =================================================== */

/* ===================================================
   PosSmart v2 Section Cards (Matches manage/purchase.vue)
   =================================================== */
.ap-card {
  border-radius: 16px !important;
  border: 1px solid #e2e8f0 !important;
  box-shadow: none !important;
  overflow: hidden;
  background-color: #ffffff !important;
}

.ap-card-title {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  font-size: 14px;
  font-weight: 600;
}

.ap-card-accent {
  width: 3px;
  height: 15px;
  border-radius: 2px;
  background: var(--v-primary-base, #5b39c9);
  display: inline-block;
  flex-shrink: 0;
}

.ap-card-subtitle {
  font-size: 11.5px;
  font-weight: 400;
  color: #94a3b8;
}

.report-main-card {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.border-bottom {
  border-bottom: 1px solid #e2e8f0 !important;
}

.border-top {
  border-top: 1px solid #e2e8f0 !important;
}

/* ===================================================
   Category / Status Chips (Horizontal Bar)
   =================================================== */
.category-chips-wrapper {
  overflow-x: auto;
  display: flex;
  align-items: center;
  padding-bottom: 2px;
}

.active-category-chip {
  color: #ffffff !important;
}

/* ===================================================
   Table Styling (Matches manage/purchase.vue .purchase-table)
   =================================================== */
.table-content-area {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
}

.purchase-table >>> .v-data-table__wrapper table {
  border-collapse: collapse;
}

.purchase-table >>> .v-data-table__wrapper table thead th {
  color: #64748b;
  font-weight: 700;
  font-size: 10.5px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  background-color: transparent !important;
  background: transparent !important;
  box-shadow: none !important;
  border-bottom: 1px solid #e2e8f0;
  height: 38px;
  white-space: nowrap;
}

.purchase-table >>> .v-data-table__wrapper table tbody tr {
  transition: background-color 0.15s ease;
}

.purchase-table >>> .v-data-table__wrapper table tbody tr:hover {
  background-color: #f8fafc !important;
}

.purchase-table >>> .v-data-table__wrapper table tbody td {
  border-bottom: 1px solid #f1f5f9;
  padding: 8px 12px;
}

.ps-row-index {
  font-size: 12px;
  color: #94a3b8;
  font-variant-numeric: tabular-nums;
  font-weight: 500;
}

.ps-product-code {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 11.5px;
  font-family: monospace;
  color: #1e293b;
  font-variant-numeric: tabular-nums;
}

.ps-copy-code {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 2px;
  border: 0;
  background: transparent;
  color: #94a3b8;
  cursor: pointer;
  border-radius: 4px;
  transition: color 0.15s ease;
}

.ps-copy-code:hover {
  color: #5b39c9;
}

/* Stock Badges (Matches stock-pill in manage/purchase) */
.stock-pill {
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 10px;
  font-weight: 700;
  line-height: 1.3;
  white-space: nowrap;
  display: inline-block;
}

.stock-pill-out {
  background-color: #fee2e2;
  color: #dc2626;
  border: 1px solid #fecdd3;
}

.stock-pill-low {
  background-color: #fef3c7;
  color: #d97706;
  border: 1px solid #fde68a;
}

.stock-pill-normal {
  background-color: #d1fae5;
  color: #047857;
  border: 1px solid #a7f3d0;
}

.ps-total-value {
  font-size: 13px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
}

.ps-total-value--danger {
  color: #dc2626;
}

.ps-price-value {
  font-size: 13px;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  color: #1e293b;
}

.font-numeric {
  font-variant-numeric: tabular-nums;
}

/* Action Buttons */
.ps-row-actions {
  display: flex;
  align-items: center;
  gap: 5px;
}

.ps-act {
  width: 28px;
  height: 28px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  color: #64748b;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  cursor: pointer;
  transition: all 0.15s ease;
  outline: none;
}

.ps-act:hover {
  color: #5b39c9;
  border-color: #5b39c9;
  background: rgba(91, 57, 201, 0.06);
}

.ps-act--danger:hover {
  color: #ef4444;
  border-color: #ef4444;
  background: #fef2f2;
}

/* Empty State */
.ps-empty {
  color: #94a3b8;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 56px 16px;
}

.ps-empty-icon {
  color: #cbd5e1 !important;
}

.ps-empty-text {
  font-size: 14px;
  color: #475569;
}

.ps-empty-subtext {
  font-size: 12px;
  color: #94a3b8;
}

/* Pagination Footer */
.table-pagination-footer {
  background-color: #ffffff;
}

.pagination-btn {
  border-color: #e2e8f0 !important;
}

.rows-select >>> .v-input__control {
  min-height: 28px !important;
}

.rows-select >>> .v-input__slot {
  min-height: 28px !important;
  font-size: 12px !important;
  padding: 0 8px !important;
}

/* ===================================================
   Custom Clean Scrollbars
   =================================================== */
.table-content-area,
.category-chips-wrapper,
.table-content-area >>> .v-data-table__wrapper {
  scrollbar-width: thin;
  scrollbar-color: #cbd5e1 transparent;
}

.table-content-area::-webkit-scrollbar,
.category-chips-wrapper::-webkit-scrollbar,
.table-content-area >>> .v-data-table__wrapper::-webkit-scrollbar {
  width: 5px;
  height: 5px;
}

.table-content-area::-webkit-scrollbar-thumb,
.category-chips-wrapper::-webkit-scrollbar-thumb,
.table-content-area >>> .v-data-table__wrapper::-webkit-scrollbar-thumb {
  background-color: #cbd5e1;
  border-radius: 999px;
}

/* ===================================================
   Dark Theme Overrides
   =================================================== */

.theme--dark .ap-card {
  border-color: rgba(255, 255, 255, 0.12) !important;
  background-color: #1e1e2d !important;
}

.theme--dark .ap-card-subtitle {
  color: #64748b;
}

.theme--dark .ps-product-code {
  color: #f1f5f9;
}

.theme--dark .ps-price-value {
  color: #94a3b8;
}

.theme--dark .purchase-table >>> .v-data-table__wrapper table thead th {
  color: #94a3b8;
  background-color: transparent !important;
  background: transparent !important;
  box-shadow: none !important;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.theme--dark .purchase-table >>> .v-data-table__wrapper table tbody td {
  border-bottom-color: rgba(255, 255, 255, 0.08);
}

.theme--dark .purchase-table >>> .v-data-table__wrapper table tbody tr:hover {
  background-color: rgba(255, 255, 255, 0.04) !important;
}

.theme--dark .table-pagination-footer {
  background-color: #1e1e2d !important;
  color: #cbd5e1;
}

.theme--dark .pagination-btn {
  background-color: #262638 !important;
  border-color: rgba(255, 255, 255, 0.12) !important;
  color: #cbd5e1 !important;
}

.theme--dark .rows-select >>> .v-input__slot {
  background-color: #262638 !important;
}

.theme--dark .rows-select >>> .v-select__selection {
  color: #f1f5f9 !important;
}

.theme--dark .rows-select >>> fieldset {
  border-color: rgba(255, 255, 255, 0.12) !important;
}

.theme--dark .border-bottom {
  border-bottom-color: rgba(255, 255, 255, 0.12) !important;
}

.theme--dark .border-top {
  border-top-color: rgba(255, 255, 255, 0.12) !important;
}

.theme--dark .ps-act {
  background-color: #252538;
  border-color: rgba(255, 255, 255, 0.12);
  color: #94a3b8;
}

.theme--dark .ps-act:hover {
  color: #818cf8;
  border-color: #818cf8;
  background-color: rgba(91, 57, 201, 0.2);
}
</style>
